import React, { useEffect, useState } from 'react';
import { X, Heart } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';

const ELIGIBLE_ROLES = ['admin', 'agent', 'seller'];

export const NPSWidget = () => {
    const { user } = useAuth();
    const [visible, setVisible] = useState(false);
    const [score, setScore] = useState(null);
    const [comment, setComment] = useState('');
    const [step, setStep] = useState('score'); // score | comment | thanks
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!user || !ELIGIBLE_ROLES.includes(user.role)) return undefined;
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const { data } = await api.get('/nps/eligibility');
                if (!cancelled && data?.show) setVisible(true);
            } catch { /* silent */ }
        }, 5000);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [user]);

    if (!visible || !user) return null;

    const isSeller = user.role === 'seller';
    const question = isSeller
        ? 'Recommanderiez-vous Homly à un proche qui vend son bien ?'
        : 'Recommanderiez-vous Homly à un confrère ?';
    const followUp = score >= 9
        ? "Qu'est-ce que vous appréciez le plus ?"
        : 'Que pourrions-nous améliorer ?';

    const pickScore = (n) => {
        setScore(n);
        setStep('comment');
    };

    const submit = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await api.post('/nps/submit', { score, comment: comment || null });
            setStep('thanks');
            setTimeout(() => setVisible(false), 2500);
        } catch {
            setVisible(false);
        } finally {
            setSubmitting(false);
        }
    };

    const dismiss = async () => {
        setVisible(false);
        try { await api.post('/nps/dismiss'); } catch { /* silent */ }
    };

    return (
        <div
            data-testid="nps-widget"
            className="fixed bottom-4 right-4 z-[70] w-[340px] max-w-[calc(100vw-2rem)] rounded-2xl border border-border bg-card text-card-foreground shadow-2xl p-4 animate-in slide-in-from-bottom-4 fade-in duration-500"
        >
            {step !== 'thanks' && (
                <button
                    data-testid="nps-close-btn"
                    onClick={dismiss}
                    aria-label="Fermer"
                    className="absolute top-2.5 right-2.5 p-1 rounded-full text-muted-foreground hover:bg-muted transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
            )}

            {step === 'score' && (
                <>
                    <p className="text-sm font-semibold pr-6 mb-3">{question}</p>
                    <div className="flex gap-1 justify-between mb-2">
                        {Array.from({ length: 11 }, (_, n) => (
                            <button
                                key={n}
                                data-testid={`nps-score-${n}`}
                                onClick={() => pickScore(n)}
                                className="w-7 h-8 rounded-md text-xs font-bold border border-border hover:bg-[#681793] hover:text-white hover:border-[#681793] transition-colors"
                            >
                                {n}
                            </button>
                        ))}
                    </div>
                    <div className="flex justify-between text-[10px] text-muted-foreground mb-2">
                        <span>Pas du tout</span>
                        <span>Absolument</span>
                    </div>
                    <button
                        data-testid="nps-later-btn"
                        onClick={dismiss}
                        className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                    >
                        Plus tard
                    </button>
                </>
            )}

            {step === 'comment' && (
                <>
                    <p className="text-sm font-semibold pr-6 mb-1">
                        Merci ! <span className="text-[#681793] dark:text-orange-400">{score}/10</span>
                    </p>
                    <p className="text-xs text-muted-foreground mb-2">{followUp}</p>
                    <textarea
                        data-testid="nps-comment-input"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={3}
                        maxLength={2000}
                        placeholder="Votre réponse (facultatif)…"
                        className="w-full text-sm rounded-lg border border-border bg-background p-2 mb-2 resize-none focus:outline-none focus:ring-2 focus:ring-[#681793]/40"
                    />
                    <button
                        data-testid="nps-submit-btn"
                        onClick={submit}
                        disabled={submitting}
                        className="w-full py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-[#681793] to-orange-500 hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                        {submitting ? 'Envoi…' : 'Envoyer'}
                    </button>
                </>
            )}

            {step === 'thanks' && (
                <div data-testid="nps-thanks" className="flex items-center gap-3 py-2">
                    <Heart className="w-6 h-6 text-orange-500 shrink-0" />
                    <p className="text-sm font-medium">Merci pour votre retour, il compte vraiment pour nous !</p>
                </div>
            )}
        </div>
    );
};

export default NPSWidget;
